import { Component, ElementRef, OnInit, ViewChild, OnDestroy } from '@angular/core';

import * as L from 'leaflet';
import 'leaflet.markercluster';

import { DEFAULT_LAT, DEFAULT_LONG } from '../../constants';
import { StationService } from '../../services/station.service';
import { StationMap } from '../../models/station';
import { BikeType } from '../../models/bike-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { ParametersService } from "../../services/parameters.service";
import { Observable, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-station-map',
  templateUrl: './station-map.component.html',
  styleUrls: ['./station-map.component.css']
})
export class StationMapComponent implements OnInit, OnDestroy {

  /** Visible stations for logged maintainer or admin */
  stationsList: StationMap[] = [];

  /** leaflet options */
  options = {
    layers: [
      L.tileLayer(environment.osmUrl,
        { maxZoom: 18, attribution: 'Open Street Map' })
    ],
    zoom: 10,
    center: L.latLng(DEFAULT_LAT, DEFAULT_LONG)
  };

  map: L.Map;

  /* binded map properties */
  center: L.LatLng = this.options.center;
  zoom: number = this.options.zoom;
  fitBounds: any;

  /** markers to display on map array */
  markers: any[] = [];

  /** Cluster for map markers */
  markerClusterGroup: L.MarkerClusterGroup;
  markerClusterOptions: L.MarkerClusterGroupOptions;

  /** used to add blur effect on map until markers are loaded */
  loading = true;

  /** Subscription to web service retrieving stations */
  subscription : Subscription;

  constructor(private stationService: StationService,
    private translateService: TranslateService,
    private paramService : ParametersService) {
  }

  /**
   * Get stations, display them and center the map
   */
  ngOnInit() {
    this.stationService.getStationsByGroups().subscribe((objet: any) => {

      this.extractStationsList(objet);      
      this.markStations();
      this.centerMap();
      this.loading = false;
      this.markerClusterGroup.addLayers(this.markers);
      this.reloadTiles();
    });

    this.subscription = Observable.interval(this.paramService.dashboardRefreshInterval).subscribe(() => this.reloadData());
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  reloadData() {
    this.stationService.getStationsByGroups().subscribe((objet: any) => {
      this.markerClusterGroup.clearLayers();
      this.stationsList = [];
      this.markers = [];

      this.extractStationsList(objet);
      this.markStations();
      this.centerMap();
      this.markerClusterGroup.addLayers(this.markers);
    });
  }

  extractStationsList(objet : any) {
    // for each groups => add their stations into the list
    for (var g in objet.groups) {
      if (objet.groups.hasOwnProperty(g)) {
        objet.groups[g].forEach(element => {
          let s = new StationMap(element);
          this.stationsList.push(s);
        });
      }
    }      
  }

  reloadTiles() {
    this.map.invalidateSize();
  }

  /**
   * Center map :
   * If center isn't stored in the local storage => center the map to fit all stations
   * Else center with previously stored information
   */
  centerMap(): void {

    if (localStorage.getItem("lastDashboardCenter")) {
      this.center = JSON.parse(localStorage.getItem("lastDashboardCenter"));
      this.zoom = JSON.parse(localStorage.getItem("lastDashboardZoom"));
    } else {
      let coordinatesList: L.LatLng[] = [];
      this.stationsList.forEach(s => coordinatesList.push(L.latLng(s.latitude, s.longitude)));

      let bounds = L.latLngBounds(coordinatesList);
      this.fitBounds = bounds;
    }
  }

  /**
   * Link events to the map :
   * On each move => store location in localStorage to be able to center the map later
   * @param map
   */
  onMapReady(map: L.Map) {
    this.map = map;
    map.on("moveend", function () {
      localStorage.setItem("lastDashboardCenter", JSON.stringify(map.getCenter()));
      localStorage.setItem("lastDashboardZoom", JSON.stringify(map.getZoom()));
    });
  }

  /**
   * Add map marker for all stations to display
   */
  markStations(): void {
    this.stationsList.forEach(s => this.addMarker(s));
  }

  markerClusterReady(group: L.MarkerClusterGroup) {
    this.markerClusterGroup = group;
  }

  /**
   * Push a marker giving information about a specific station
   * to the markers array
   * @param s
   */
  addMarker(s: StationMap) {
    
    const newMarker = L.marker([s.latitude, s.longitude],
      {
        icon: L.icon({
          iconSize: [41, 51],
          iconAnchor: [20, 51],
          popupAnchor: [0, -51],
          iconUrl: this.iconUrl(s.countAvailableBikes(), s.countAPs(), s.disabled, s.countOutOfOrder() > 0)
        })
      }
    );

    let popupOptions = {
      'className' : 'custom-popup'
    }

    let popup = new L.Popup();
    popup.setLatLng(newMarker.getLatLng());
    newMarker.bindPopup(popup, popupOptions);

    let that = this;
    newMarker.on("mouseover", function (e) {
      newMarker.getPopup().setContent(that.buildPopup(s))
      newMarker.openPopup();
    })

    newMarker.on("mouseout", function (e) {
      newMarker.closePopup();
    })

    this.markers.push(newMarker);
  }

  /**
   * Return icon marker url according to the rate of occupied attachment point
   * @param lockedAttachmentPoints
   * @param totalAttachmentPoints
   */
  iconUrl(availableBikes: number, totalAttachmentPoints: number, disabled : boolean, containsOutOfOrder : boolean) {
    let rate : number;
    if (totalAttachmentPoints == 0) {
      rate = 0;
    } else {
      rate = availableBikes * 1.0 / totalAttachmentPoints;
    }
    let baseUrl = "/assets/marker_";

    if (containsOutOfOrder) {
      baseUrl += "red_";
    } else if (disabled) {
      baseUrl += "greyscale_";
    }

    if (rate == 0) {
      return baseUrl + "0.png";
    } else if (rate <= 0.25) {
      return baseUrl + "25.png";
    } else if (rate <= 0.50) {
      return baseUrl + '50.png';
    } else if (rate <= 0.75) {
      return baseUrl + "75.png";
    } else {
      return baseUrl + '100.png';
    }
  }

  /**
   * Build the string contained in popup
   * @param s
   */
  buildPopup(s: StationMap): string {
    let content = '<b>' + s.name + "</b><hr class='hr-light'><b>" + s.countAPs() + " "
      + (s.countAPs() > 1 ? this.translateService.instant('dashboard.aps') : this.translateService.instant('dashboard.ap'))
      + "<br><br>" + s.countAvailableBikes() + " "
     + (s.countAvailableBikes() > 1 ? this.translateService.instant('dashboard.avbikes') : this.translateService.instant('dashboard.avbike')) + "</b>";


    for (const type in s.availableBikes) {
      if (s.availableBikes.hasOwnProperty(type)) {
        content += "<br>" + s.availableBikes[type] + " " + this.getTypeLabel(type, s.availableBikes[type]);
      }
    }

    const countOutOfOrder = s.countOutOfOrder();

    if (countOutOfOrder > 0) {
      content += '<br><br><span class=\'red\'><b>' + countOutOfOrder + ' '
      + (countOutOfOrder > 1 ? this.translateService.instant('dashboard.outoforders') : this.translateService.instant('dashboard.outoforder')) + '</b>'
      for (const type in s.outOfOrderBikes) {
        if (s.outOfOrderBikes.hasOwnProperty(type)) {
          content += '<br>' + s.outOfOrderBikes[type] + ' ' + this.getTypeLabel(type, s.outOfOrderBikes[type]);
        }
        content += '</span>';
      }
    }
    return content;
  }

  /**
   * Getting label for a bike type
   */
  getTypeLabel(type: string, sum: number): string {
    switch (type) {
      case BikeType[BikeType.CHILD]:
        return sum > 1 ? this.translateService.instant('biketype.children') : this.translateService.instant('biketype.child');
      case BikeType[BikeType.E_CARGO]:
        return sum > 1 ? this.translateService.instant('biketype.ecargos') : this.translateService.instant('biketype.ecargo');
      case BikeType[BikeType.ELECTRICAL]:
        return sum > 1 ? this.translateService.instant('biketype.electricals') : this.translateService.instant('biketype.electrical');
      case BikeType[BikeType.M_CARGO]:
        return sum > 1 ? this.translateService.instant('biketype.cargos') : this.translateService.instant('biketype.cargo');
      case BikeType[BikeType.MECHANICAL]:
        return sum > 1 ? this.translateService.instant('biketype.mechanicals') : this.translateService.instant('biketype.mechanical');
      default:
        return sum > 1 ? this.translateService.instant('biketype.undefineds') : this.translateService.instant('biketype.undefined');
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/dashboard/station-map/station-map.component.ts